import { supabase } from '@/integrations/supabase/client';
import { calculateLineItemSimilarity } from './vendorInvoiceLineItemGrouping';

export interface AutoMappingResult {
  lineItem: any;
  clusterId: string;
  mappingId: string;
  confidence: number;
  autoApplied: boolean;
}

/**
 * Match new invoice line items to existing clusters using fuzzy matching
 */
export async function matchLineItemToCluster(
  companyId: string,
  vendorId: string,
  lineItem: any,
  confidenceThreshold: number = 0.85
): Promise<AutoMappingResult | null> {
  // Get all confirmed clusters for this vendor
  const { data: clusters, error } = await supabase
    .from('invoice_line_item_clusters')
    .select('id, cluster_name, raw_line_items, confidence_score')
    .eq('company_id', companyId)
    .eq('vendor_id', vendorId)
    .eq('is_confirmed', true);

  if (error) throw error;
  if (!clusters || clusters.length === 0) return null;

  // Find best matching cluster
  let bestMatch: { cluster: any; confidence: number } | null = null;

  for (const cluster of clusters) {
    // Compare with first item in cluster as representative
    const representativeItem = cluster.raw_line_items?.[0];
    if (!representativeItem) continue;

    const similarity = calculateLineItemSimilarity(lineItem, representativeItem);
    
    if (!bestMatch || similarity > bestMatch.confidence) {
      bestMatch = {
        cluster,
        confidence: similarity,
      };
    }
  }

  if (!bestMatch || bestMatch.confidence < confidenceThreshold) {
    return null;
  }

  // Get the mapping for this cluster
  const { data: mapping } = await supabase
    .from('vendor_ingredient_mappings')
    .select('id')
    .eq('company_id', companyId)
    .eq('vendor_id', vendorId)
    .eq('cluster_id', bestMatch.cluster.id)
    .single();

  if (!mapping) return null;

  return {
    lineItem,
    clusterId: bestMatch.cluster.id,
    mappingId: mapping.id,
    confidence: bestMatch.confidence,
    autoApplied: bestMatch.confidence >= confidenceThreshold,
  };
}

/**
 * Auto-map ingredients based on confidence scores
 */
export async function autoMapInvoiceLineItems(
  companyId: string,
  vendorId: string,
  lineItems: any[],
  confidenceThreshold: number = 0.85
): Promise<{
  autoMapped: AutoMappingResult[];
  flaggedForReview: AutoMappingResult[];
}> {
  const autoMapped: AutoMappingResult[] = [];
  const flaggedForReview: AutoMappingResult[] = [];

  for (const lineItem of lineItems) {
    const match = await matchLineItemToCluster(companyId, vendorId, lineItem, confidenceThreshold);

    if (!match) continue;

    if (match.confidence >= confidenceThreshold) {
      autoMapped.push(match);
    } else {
      flaggedForReview.push(match);
    }
  }

  return { autoMapped, flaggedForReview };
}

/**
 * Apply auto-mapping to a new invoice
 */
export async function applyAutoMappingToInvoice(
  companyId: string,
  vendorId: string,
  invoiceId: string
): Promise<{
  autoMapped: number;
  flaggedForReview: number;
}> {
  // Get invoice line items
  const { data: invoice, error: invoiceError } = await supabase
    .from('vendor_invoice_training')
    .select('line_items')
    .eq('id', invoiceId)
    .single();

  if (invoiceError || !invoice) {
    throw new Error('Invoice not found');
  }

  const lineItems = invoice.line_items || [];
  if (lineItems.length === 0) {
    return { autoMapped: 0, flaggedForReview: 0 };
  }

  // Auto-map line items
  const { autoMapped, flaggedForReview } = await autoMapInvoiceLineItems(
    companyId,
    vendorId,
    lineItems
  );

  // Store auto-mapping results (could create a new table for this)
  // For now, we'll just return the counts

  return {
    autoMapped: autoMapped.length,
    flaggedForReview: flaggedForReview.length,
  };
}

/**
 * Update price history automatically when new invoices are processed
 */
export async function updatePriceHistoryFromAutoMapping(
  companyId: string,
  vendorId: string,
  invoiceId: string
): Promise<void> {
  // Get invoice
  const { data: invoice, error } = await supabase
    .from('vendor_invoice_training')
    .select('line_items, invoice_date')
    .eq('id', invoiceId)
    .single();

  if (error || !invoice) return;

  // Get auto-mapped items
  const { autoMapped } = await applyAutoMappingToInvoice(companyId, vendorId, invoiceId);

  // For each auto-mapped item, update price history
  for (const mapping of autoMapped) {
    // Get mapping details
    const { data: mappingData } = await supabase
      .from('vendor_ingredient_mappings')
      .select('ingredient_id, price_per_unit, unit_of_measure')
      .eq('id', mapping.mappingId)
      .single();

    if (mappingData && mappingData.ingredient_id) {
      // Extract price from line item
      const price = mapping.lineItem.price || mapping.lineItem.unitPrice || mappingData.price_per_unit;
      const unit = mapping.lineItem.unit || mappingData.unit_of_measure || 'each';

      // Update price history
      const { updatePriceHistoryFromInvoice } = await import('./vendorInvoicePriceHistory');
      await updatePriceHistoryFromInvoice(
        companyId,
        mappingData.ingredient_id,
        price,
        unit,
        invoice.invoice_date,
        vendorId
      );
    }
  }
}

